import {
  S3Client,
  GetObjectCommand,
  HeadObjectCommand,
  PutObjectCommand,
  DeleteObjectCommand,
} from "@aws-sdk/client-s3";
import { fromCognitoIdentityPool } from "@aws-sdk/credential-providers";
import { environment } from "src/environments/environment";

const REGION = "eu-west-3";
const IDENTITY_POOL_ID = environment.identityPoolId;
const BUCKET = environment.bucket;

async function bodyToText(body: any): Promise<string> {
  if (typeof body?.transformToString === "function") {
    return body.transformToString();
  }

  return new Promise((resolve, reject) => {
    const chunks: Uint8Array[] = [];
    body.on("data", (chunk: Uint8Array) => chunks.push(chunk));
    body.on("error", reject);
    body.on("end", () => {
      const decoder = new TextDecoder("utf-8");
      const totalLength = chunks.reduce((sum, chunk) => sum + chunk.length, 0);
      const merged = new Uint8Array(totalLength);

      let offset = 0;
      for (const chunk of chunks) {
        merged.set(chunk, offset);
        offset += chunk.length;
      }

      resolve(decoder.decode(merged));
    });
  });
}

async function bodyToUint8Array(body: any): Promise<Uint8Array> {
  if (typeof body?.transformToByteArray === "function") {
    const bytes = await body.transformToByteArray();
    return new Uint8Array(bytes);
  }

  return new Promise((resolve, reject) => {
    const chunks: Uint8Array[] = [];
    body.on("data", (chunk: Uint8Array) => chunks.push(chunk));
    body.on("error", reject);
    body.on("end", () => {
      const totalLength = chunks.reduce((sum, chunk) => sum + chunk.length, 0);
      const merged = new Uint8Array(totalLength);

      let offset = 0;
      for (const chunk of chunks) {
        merged.set(chunk, offset);
        offset += chunk.length;
      }

      resolve(merged);
    });
  });
}

export class S3Service {
  private client: S3Client;
  private objectExistence = new Map<string, Promise<boolean>>();

  constructor() {
    this.client = new S3Client({
      region: REGION,
      credentials: fromCognitoIdentityPool({
        clientConfig: { region: REGION },
        identityPoolId: IDENTITY_POOL_ID,
      }),
    });
  }

  async objectExists(key: string | null | undefined): Promise<boolean> {
    const normalized = (key || '').trim();
    if (
      !normalized ||
      normalized === 'null' ||
      normalized === 'undefined' ||
      normalized.endsWith('/null') ||
      normalized.endsWith('/undefined')
    ) {
      return false;
    }

    const cached = this.objectExistence.get(normalized);
    if (cached) return cached;

    const check = this.client
      .send(new HeadObjectCommand({ Bucket: BUCKET, Key: normalized }))
      .then(() => true)
      .catch(() => false);
    this.objectExistence.set(normalized, check);
    return check;
  }

  /**
   * Récupère un objet texte (txt, html, json, csv...)
   */
  async getTextObject(key: string): Promise<string> {
    const command = new GetObjectCommand({
      Bucket: BUCKET,
      Key: key,
    });

    const response = await this.client.send(command);

    if (!response.Body) {
      throw new Error(`Objet vide pour la clé ${key}`);
    }

    return bodyToText(response.Body);
  }

  /**
   * Récupère un objet JSON et le parse
   */
  async getJsonObject<T = any>(key: string): Promise<T> {
    const text = await this.getTextObject(key);
    return JSON.parse(text) as T;
  }

  /**
   * Récupère un objet binaire (image, pdf, doc...)
   */
  async getObject(key: string): Promise<{
    body: Uint8Array;
    contentType?: string;
    contentLength?: number;
    lastModified?: Date;
    etag?: string;
  }> {
    const command = new GetObjectCommand({
      Bucket: BUCKET,
      Key: key,
    });

    const response = await this.client.send(command);

    if (!response.Body) {
      throw new Error(`Objet vide pour la clé ${key}`);
    }

    const body = await bodyToUint8Array(response.Body);

    return {
      body,
      contentType: response.ContentType,
      contentLength: response.ContentLength,
      lastModified: response.LastModified,
      etag: response.ETag,
    };
  }

  /**
   * Récupère un Blob directement exploitable côté navigateur
   */
  async getObjectAsBlob(key: string): Promise<Blob> {
    const result = await this.getObject(key);

    return new Blob([new Uint8Array(result.body)], {
      type: result.contentType || "application/octet-stream",
    });
  }

  /**
   * Récupère une URL locale temporaire (blob URL) pratique pour afficher une image ou ouvrir un PDF
   */
  async getObjectUrl(key: string): Promise<string> {
    const blob = await this.getObjectAsBlob(key);
    return URL.createObjectURL(blob);
  }

  /**
   * Upload fichier
   */
  async putFile(params: {
    key: string;
    body: Blob | File | Uint8Array | string;
    contentType?: string;
  }): Promise<boolean> {
    const command = new PutObjectCommand({
      Bucket: BUCKET,
      Key: params.key,
      Body: params.body,
      ContentType: params.contentType,
    });

    await this.client.send(command);
    return true;
  }

  /**
   * Upload texte
   */
  async putText(
    key: string,
    body: string,
    contentType = "text/plain; charset=utf-8"
  ): Promise<boolean> {
    const command = new PutObjectCommand({
      Bucket: BUCKET,
      Key: key,
      Body: body,
      ContentType: contentType,
    });

    await this.client.send(command);
    return true;
  }

  /**
   * Suppression objet
   */
  async deleteObject(key: string): Promise<boolean> {
    const command = new DeleteObjectCommand({
      Bucket: BUCKET,
      Key: key,
    });

    await this.client.send(command);
    return true;
  }
}
